import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import Image from '../Image';
import Typography from '../Typography';
import Placeholder from './placeholder.svg';

const IMG_SIZE = {
  huge: 260,
  large: 180,
  medium: 140,
  small: 100,
  tiny: 80,
};

function getRootClasses({ classes, hidden, className, onClick, alignLeft, data: { url } }) {
  return classNames(
    classes.root,
    hidden && classes.rootHidden,
    alignLeft && classes.rootAlignLeft,
    (onClick || url) && classes.rootClickable,
    className,
  );
}

function getBadgeClasses(classes, imageStyle) {
  return classNames(classes.badge, imageStyle !== 'circle' && classes.badgeOffset);
}

function textIsWide(imageSize) {
  return imageSize === 'huge';
}

function textIsNarrow(imageSize) {
  return imageSize === 'tiny';
}

function getTitleClasses(classes, imageSize) {
  return classNames(
    classes.title,
    textIsWide(imageSize) && classes.textWide,
    textIsNarrow(imageSize) && classes.textNarrow,
  );
}

function getSubtitleClasses(classes, imageSize) {
  return classNames(
    classes.subtitle,
    textIsWide(imageSize) && classes.textWide,
    textIsNarrow(imageSize) && classes.textNarrow,
  );
}

function getInteractiveProps({ onClick, data: { url } }) {
  if (onClick) {
    return {
      onClick,
      tabIndex: 0,
      role: 'tab',
    };
  }
  if (url) {
    return {
      href: url,
      target: '_blank',
    };
  }
  return false;
}

function GridItem(props) {
  const {
    classes,
    imageStyle,
    imageSize,
    data,
    titleTag,
    largeTitle,
    onClick,
    styles,
    textColors,
    hideTitle,
    placeholder,
    imageRatio,
  } = props;
  const { image, badge, title, subtitle, url } = data;

  if ((!image || !image.url) && !title && !subtitle) {
    return null;
  }

  const titleColor = styles.item && styles.item.title;
  const subtitleColor = styles.item && styles.item.subtitle;

  const RootComponent = url && !onClick ? 'a' : 'li';
  return (
    <RootComponent className={getRootClasses(props)} {...getInteractiveProps(props)}>
      <div className={classes.imageWrapper}>
        <Image
          className={classes.image}
          type={imageStyle}
          alt={title}
          src={image && image.url}
          placeholder={placeholder || Placeholder}
          style={{
            width: imageRatio * IMG_SIZE[imageSize],
            height: IMG_SIZE[imageSize],
          }}
        />
        {image && image.url && badge && (
          <div className={getBadgeClasses(classes, imageStyle)}>
            <img
              src={badge.url}
              alt={badge.alt || `${title} badge`}
              className={classes.badgeImage}
            />
            <div className={classes.badgeBorder} />
          </div>
        )}
      </div>
      {title && !hideTitle && (
        <Typography
          className={getTitleClasses(classes, imageSize)}
          type={largeTitle ? 'headline' : 'title'}
          style={{ ...textColors.text, ...titleColor }}
          component={titleTag}
        >
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography
          className={getSubtitleClasses(classes, imageSize)}
          style={{ ...textColors.text, ...subtitleColor }}
          component="p"
        >
          {typeof subtitle === 'string' ? subtitle : subtitle.join('\n')}
        </Typography>
      )}
    </RootComponent>
  );
}

GridItem.propTypes = {
  classes: PropTypes.object.isRequired,
  textColors: PropTypes.object.isRequired,
  className: PropTypes.string,
  imageStyle: PropTypes.oneOf(['square', 'rectangular', 'circle', 'rounded']),
  imageSize: PropTypes.oneOf(Object.keys(IMG_SIZE)),
  data: PropTypes.shape({
    image: PropTypes.shape({
      url: PropTypes.string.isRequired,
    }),
    badge: PropTypes.shape({
      url: PropTypes.string.isRequired,
      alt: PropTypes.string,
    }),
    title: PropTypes.string,
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  }).isRequired,
  titleTag: PropTypes.oneOf(['h2', 'h3', 'h4', 'h5', 'h6']),
  alignLeft: PropTypes.bool,
  largeTitle: PropTypes.bool,
  onClick: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  hidden: PropTypes.bool,
  styles: PropTypes.object.isRequired,
  hideTitle: PropTypes.bool,
  placeholder: PropTypes.string,
  imageRatio: PropTypes.number,
};

GridItem.defaultProps = {
  className: null,
  imageStyle: null,
  imageSize: 'medium',
  badge: null,
  title: null,
  titleTag: 'h3',
  alignLeft: false,
  largeTitle: false,
  subtitle: null,
  hidden: false,
  onClick: false,
  image: null,
  hideTitle: false,
  placeholder: Placeholder,
  imageRatio: 1,
};

const stylesheet = theme => ({
  root: {
    padding: 2 * theme.spacing.unit,
    margin: theme.spacing.unit,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    borderRadius: 8,
    outline: 'none',
    textDecoration: 'none',
    [theme.breakpoints.down('sm')]: {
      padding: 2.5 * theme.spacing.unit,
      margin: 0,
    },
    [theme.breakpoints.down('xs')]: {
      padding: [[2.5 * theme.spacing.unit, 0]],
    },
  },
  rootHidden: {
    display: 'none',
  },
  rootClickable: {
    cursor: 'pointer',
    willChange: 'background-color',
    transition: `background-color ${theme.transitions.duration.shortest}ms`,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.05)',
    },
    '&:focus': {
      backgroundColor: 'rgba(0,0,0,0.1)',
    },
  },
  rootAlignLeft: {
    textAlign: 'left',
  },
  imageWrapper: {
    margin: '0 auto',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    margin: '0 auto',
    objectFit: 'contain',
  },
  badge: {
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: theme.palette.common.faintBlack,
    height: '35%',
    width: '35%',
    position: 'absolute',
    bottom: '-2.5%',
    right: '-2.5%',
  },
  badgeOffset: {
    right: '-7.5%',
    bottom: '-7.5%',
  },
  badgeImage: {
    width: '100%',
    height: '100%',
    display: 'block',
    userSelect: 'none',
  },
  badgeBorder: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    top: 0,
    left: 0,
    position: 'absolute',
    border: [[2, 'solid', 'rgba(125, 125, 125, 0.333)']],
  },
  title: {
    color: theme.palette.common.text,
    margin: [2 * theme.spacing.unit, 'auto', 0],
    width: 200,
    wordWrap: 'break-word',
  },
  subtitle: {
    margin: [theme.spacing.unit, 'auto', 0],
    wordWrap: 'break-word',
    width: 200,
    whiteSpace: 'pre-wrap',
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
  },
  textWide: {
    width: IMG_SIZE.huge,
  },
  textNarrow: {
    width: 2 * IMG_SIZE.tiny,
  },
});

export default injectSheet(stylesheet)(GridItem);



// WEBPACK FOOTER //
// ./src/site-render/src/Grid/GridItem.js